import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaUserFriends,
  FaSync,
  FaExclamationTriangle,
  FaClipboardList,
  FaHandPaper,
  FaUnlock,
  FaClock,
  FaTasks,
  FaPlayCircle,
  FaUsersCog,
  FaCalendarPlus,
} from "react-icons/fa";
import api from "../api";
import Modal from "../components/Modal";

const ROLE_LABELS = {
  assignee: "Responsável atual",
  responsible: "Responsável potencial",
  requester: "Solicitante",
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}



export default function MyTeam({ refreshKey }) {
  const [team, setTeam] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedMember, setSelectedMember] = useState("all");
  const [selectedProcess, setSelectedProcess] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    allDay: false,
    notifyOnDashboard: true,
  });
  const [eventSaving, setEventSaving] = useState(false);
  const [eventError, setEventError] = useState("");
  const [eventSuccess, setEventSuccess] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("total");

  const fetchData = useCallback(async () => {
    const firstLoad = !hasLoadedRef.current;
    if (firstLoad) setLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const [teamResponse, instancesResponse] = await Promise.all([
        api.get("/users/my-team"),
        api.get("/processes/instances/team?summary=1"),
      ]);
      setTeam(teamResponse.data || null);
      setInstances(Array.isArray(instancesResponse.data) ? instancesResponse.data : []);
      hasLoadedRef.current = true;
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível carregar os dados da equipe.";
      if (firstLoad) {
        setError(message);
        setTeam(null);
        setInstances([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  useEffect(() => {
    if (!eventSuccess) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      setEventSuccess("");
    }, 6000);
    return () => clearTimeout(timeout);
  }, [eventSuccess]);

  const memberMap = useMemo(() => {
    const map = new Map();
    const members = Array.isArray(team?.members) ? team.members : [];
    members.forEach((member) => {
      map.set(member.id, member);
    });
    return map;
  }, [team?.members]);

  const summary = useMemo(() => {
    const now = Date.now();
    return instances.reduce(
      (acc, instance) => {
        const status = typeof instance?.status === "string" ? instance.status.toUpperCase() : "";
        acc.total += 1;
        if (status === "COMPLETED") {
          acc.completed += 1;
        } else {
          acc.inProgress += 1;
        }
        if (status === "ACTIVE") {
          const deadline = instance?.currentStepDeadline ? new Date(instance.currentStepDeadline) : null;
          if (deadline && !Number.isNaN(deadline.getTime()) && deadline.getTime() < now) {
            acc.overdue += 1;
          }
          const context = instance?.supervisorContext || {};
          if (context.canClaim) {
            acc.available += 1;
          }
        }
        return acc;
      },
      { total: 0, inProgress: 0, completed: 0, overdue: 0, available: 0 },
    );
  }, [instances]);

  const decoratedTasks = useMemo(() => {
    const list = instances.map((instance) => {
      const context = instance?.supervisorContext || {};
      const subordinates = Array.isArray(context.subordinates) ? context.subordinates : [];
      const decoratedSubordinates = subordinates.map((entry) => ({
        ...entry,
        member: memberMap.get(entry.id) || null,
      }));
      const status = typeof instance?.status === "string" ? instance.status.toUpperCase() : "";
      const isCompleted = status === "COMPLETED";
      const stepIndex =
        typeof instance?.currentStep === "number"
          ? instance.currentStep
          : instance?.currentStep ?? 0;
      const defaultStepLabel = `Etapa ${Number(stepIndex) + 1}`;
      const stepName = isCompleted
        ? "Processo concluído"
        : instance?.process?.stepNames?.[String(stepIndex)] || defaultStepLabel;
      const deadline = instance?.currentStepDeadline ? new Date(instance.currentStepDeadline) : null;
      const deadlineTime = deadline && !Number.isNaN(deadline.getTime()) ? deadline.getTime() : null;
      const endedAt = instance?.endedAt ? new Date(instance.endedAt) : null;
      const endedAtTime = endedAt && !Number.isNaN(endedAt.getTime()) ? endedAt.getTime() : null;
      const isOverdue = !isCompleted && typeof deadlineTime === "number" ? deadlineTime < Date.now() : false;
      const isAvailable = !isCompleted && Boolean(context.canClaim);
      const startedAt = instance?.startedAt ? new Date(instance.startedAt) : null;
      const startedAtTime = startedAt && !Number.isNaN(startedAt.getTime()) ? startedAt.getTime() : null;
      const durationMs =
        typeof endedAtTime === "number" && typeof startedAtTime === "number" && endedAtTime >= startedAtTime
          ? endedAtTime - startedAtTime
          : null;

      return {
        instance,
        context,
        subordinates: decoratedSubordinates,
        stepName,
        deadline,
        deadlineTime,
        endedAt,
        endedAtTime,
        startedAt,
        startedAtTime,
        durationMs,
        isOverdue,
        isAvailable,
        status,
        statusCategory: isCompleted ? "completed" : "inProgress",
      };
    });

    const filtered = list.filter((entry) => {
      if (entry.statusCategory === "completed") {
        return true;
      }
      return entry.subordinates.length || entry.context.canClaim || entry.context.canRelease;
    });

    return filtered.sort((a, b) => {
      const timeA =
        a.statusCategory === "completed"
          ? typeof a.endedAtTime === "number"
            ? a.endedAtTime
            : Number.POSITIVE_INFINITY
          : typeof a.deadlineTime === "number"
          ? a.deadlineTime
          : Number.POSITIVE_INFINITY;
      const timeB =
        b.statusCategory === "completed"
          ? typeof b.endedAtTime === "number"
            ? b.endedAtTime
            : Number.POSITIVE_INFINITY
          : typeof b.deadlineTime === "number"
          ? b.deadlineTime
          : Number.POSITIVE_INFINITY;
      if (timeA === timeB) {
        return a.instance.id - b.instance.id;
      }
      return timeA - timeB;
    });
  }, [instances, memberMap]);

  const performanceByMember = useMemo(() => {
    const map = new Map();
    decoratedTasks.forEach((task) => {
      task.subordinates.forEach((entry) => {
        const memberId = entry.id;
        if (!memberId) {
          return;
        }
        const current = map.get(memberId) || {
          total: 0,
          inProgress: 0,
          overdue: 0,
          completed: 0,
        };
        current.total += 1;
        if (task.statusCategory === "completed") {
          current.completed += 1;
        } else {
          current.inProgress += 1;
          if (task.isOverdue) {
            current.overdue += 1;
          }
        }
        map.set(memberId, current);
      });
    });
    return map;
  }, [decoratedTasks]);

  const members = useMemo(() => {
    const list = Array.isArray(team?.members) ? team.members.slice() : [];
    return list
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((member) => {
        const stats = performanceByMember.get(member.id) || {
          total: 0,
          inProgress: 0,
          overdue: 0,
          completed: 0,
        };
        const base = stats.inProgress + stats.completed;
        const punctuality = base > 0 ? Math.round(((base - stats.overdue) / base) * 100) : null;
        return {
          ...member,
          taskCount: stats.inProgress,
          performance: {
            ...stats,
            punctuality,
          },
        };
      });
  }, [performanceByMember, team?.members]);

  const membersPerformanceRanking = useMemo(() => {
    return members
      .slice()
      .sort((a, b) => {
        const overdueDiff = (b.performance?.overdue || 0) - (a.performance?.overdue || 0);
        if (overdueDiff !== 0) {
          return overdueDiff;
        }
        const progressDiff = (b.performance?.inProgress || 0) - (a.performance?.inProgress || 0);
        if (progressDiff !== 0) {
          return progressDiff;
        }
        return a.name.localeCompare(b.name);
      });
  }, [members]);

  const memberIds = useMemo(
    () =>
      members
        .map((member) => Number(member.id))
        .filter((id) => Number.isInteger(id) && id > 0),
    [members],
  );

  const canCreateTeamEvent = memberIds.length > 0;

  useEffect(() => {
    if (selectedMember === "all") {
      return;
    }
    const exists = members.some((member) => String(member.id) === String(selectedMember));
    if (!exists) {
      setSelectedMember("all");
    }
  }, [members, selectedMember]);

  const activeTasks = useMemo(
    () => decoratedTasks.filter((task) => task.statusCategory === "inProgress"),
    [decoratedTasks],
  );

  const processOptions = useMemo(() => {
    const map = new Map();
    activeTasks.forEach((task) => {
      const process = task.instance?.process;
      if (!process?.id) return;
      map.set(String(process.id), process.name || `Processo #${process.id}`);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
  }, [activeTasks]);

  useEffect(() => {
    if (selectedProcess === "all") return;
    if (!processOptions.some((process) => process.id === String(selectedProcess))) {
      setSelectedProcess("all");
    }
  }, [processOptions, selectedProcess]);

  const filteredTasks = useMemo(() => {
    let list = activeTasks;

    if (selectedStatus === "inProgress") {
      list = list.filter((task) => task.statusCategory === "inProgress");
    } else if (selectedStatus === "overdue") {
      list = list.filter((task) => task.isOverdue);
    } else if (selectedStatus === "available") {
      list = list.filter((task) => task.isAvailable);
    } else if (selectedStatus === "completed") {
      list = [];
    }

    if (selectedMember !== "all") {
      const memberId = Number(selectedMember);
      if (Number.isFinite(memberId)) {
        list = list.filter((task) =>
          task.subordinates.some((entry) =>
            entry.id === memberId &&
            (entry.roles || []).some((role) => role === "assignee" || role === "responsible"),
          ),
        );
      }
    }

    if (selectedProcess !== "all") {
      list = list.filter(
        (task) => String(task.instance?.process?.id || "") === String(selectedProcess),
      );
    }

    return list;
  }, [activeTasks, selectedMember, selectedProcess, selectedStatus]);

  const totalActiveTasks = summary.inProgress;
  const hasMembers = members.length > 0;

  const handleClaim = useCallback(async (instanceId) => {
    if (!instanceId || actionLoading === instanceId) return;
    setActionLoading(instanceId);
    try {
      const response = await api.post(`/processes/instances/${instanceId}/claim`);
      const updated = response?.data && typeof response.data === "object" ? response.data : {};
      setInstances((current) =>
        current.map((instance) => {
          if (Number(instance?.id) !== Number(instanceId)) return instance;
          const hasAssignee = Object.prototype.hasOwnProperty.call(updated, "currentAssignee");
          const subordinates = Array.isArray(instance.supervisorContext?.subordinates)
            ? instance.supervisorContext.subordinates.map((entry) => ({
                ...entry,
                roles: Array.isArray(entry.roles)
                  ? entry.roles.filter((role) => role !== "assignee")
                  : [],
              }))
            : [];
          return {
            ...instance,
            status: updated.status || instance.status,
            currentStep: updated.currentStep ?? instance.currentStep,
            currentStepDeadline: updated.currentStepDeadline ?? instance.currentStepDeadline,
            endedAt: updated.endedAt ?? instance.endedAt,
            currentAssignee: hasAssignee ? updated.currentAssignee : instance.currentAssignee,
            supervisorContext: {
              ...(instance.supervisorContext || {}),
              subordinates,
              canClaim: false,
              canRelease: false,
            },
          };
        }),
      );
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível assumir a tarefa.";
      alert(message);
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading]);

  const handleRelease = useCallback(async (instanceId) => {
    if (!instanceId || actionLoading === instanceId) return;
    setActionLoading(instanceId);
    try {
      const response = await api.post(`/processes/instances/${instanceId}/reassign`, { userId: null });
      const updated = response?.data && typeof response.data === "object" ? response.data : {};
      setInstances((current) =>
        current.map((instance) => {
          if (Number(instance?.id) !== Number(instanceId)) return instance;
          const subordinates = Array.isArray(instance.supervisorContext?.subordinates)
            ? instance.supervisorContext.subordinates.map((entry) => ({
                ...entry,
                roles: Array.isArray(entry.roles)
                  ? entry.roles.filter((role) => role !== "assignee")
                  : [],
              }))
            : [];
          const hasResponsible = subordinates.some((entry) =>
            Array.isArray(entry.roles) && entry.roles.includes("responsible"),
          );
          const hasAssignee = Object.prototype.hasOwnProperty.call(updated, "currentAssignee");
          const nextAssignee = hasAssignee ? updated.currentAssignee : null;
          return {
            ...instance,
            status: updated.status || instance.status,
            currentStep: updated.currentStep ?? instance.currentStep,
            currentStepDeadline: updated.currentStepDeadline ?? instance.currentStepDeadline,
            endedAt: updated.endedAt ?? instance.endedAt,
            currentAssignee: nextAssignee,
            supervisorContext: {
              ...(instance.supervisorContext || {}),
              subordinates,
              canClaim: !nextAssignee && hasResponsible,
              canRelease: false,
            },
          };
        }),
      );
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível liberar a tarefa.";
      alert(message);
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading]);

  const resetEventForm = useCallback(() => {
    setEventForm({
      title: "",
      description: "",
      date: "",
      time: "",
      allDay: false,
      notifyOnDashboard: true,
    });
    setEventError("");
  }, []);

  const openTeamEventModal = useCallback(() => {
    resetEventForm();
    setEventModalOpen(true);
  }, [resetEventForm]);

  const closeTeamEventModal = useCallback(() => {
    if (eventSaving) {
      return;
    }
    setEventModalOpen(false);
    setEventError("");
  }, [eventSaving]);

  const handleEventFieldChange = useCallback((field, value) => {
    setEventForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleToggleAllDay = useCallback((checked) => {
    setEventForm((prev) => ({
      ...prev,
      allDay: checked,
      time: checked ? "" : prev.time,
    }));
  }, []);

  const handleSubmitTeamEvent = useCallback(
    async (event) => {
      event.preventDefault();
      if (eventSaving) return;
      setEventError("");
      const title = eventForm.title.trim();
      if (!title) {
        setEventError("Informe um título para o evento da equipe.");
        return;
      }
      if (!eventForm.date) {
        setEventError("Informe a data do evento da equipe.");
        return;
      }
      let dateValue = eventForm.date;
      if (!eventForm.allDay) {
        if (!eventForm.time) {
          setEventError("Informe o horário do evento da equipe.");
          return;
        }
        dateValue = `${eventForm.date}T${eventForm.time}`;
      }
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) {
        setEventError("Data ou horário do evento inválido.");
        return;
      }
      if (!memberIds.length) {
        setEventError("Nenhum integrante encontrado para receber o evento.");
        return;
      }
      const uniqueTargetIds = Array.from(new Set(memberIds));
      setEventSaving(true);
      try {
        await api.post("/calendar/events", {
          title,
          description: eventForm.description.trim() ? eventForm.description.trim() : undefined,
          date: dateValue,
          allDay: Boolean(eventForm.allDay),
          notifyOnDashboard: Boolean(eventForm.notifyOnDashboard),
          targetUserIds: uniqueTargetIds,
        });
        setEventSuccess("Evento da equipe criado e enviado para todos os integrantes!");
        setEventModalOpen(false);
        resetEventForm();
      } catch (err) {
        const message =
          err?.response?.data?.error || "Não foi possível criar o evento da equipe.";
        setEventError(message);
      } finally {
        setEventSaving(false);
      }
    },
    [eventForm, eventSaving, memberIds, resetEventForm],
  );

  const handleStatusCardClick = useCallback((key) => {
    setSelectedStatus((prev) => {
      if (key === "total") {
        return "total";
      }
      return prev === key ? "total" : key;
    });
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Equipe</p>
            <h1 className="text-3xl font-semibold text-slate-900">Minha equipe</h1>
          </div>
        </header>
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
          Carregando equipe...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Equipe</p>
            <h1 className="text-3xl font-semibold text-slate-900">Minha equipe</h1>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50"
          >
            <FaSync className="animate-spin text-emerald-500" />
            Recarregar
          </button>
        </header>
        <div className="flex items-center gap-3 rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <FaExclamationTriangle className="text-xl" />
          <div>
            <p className="font-semibold">Falha ao carregar a equipe.</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Equipe</p>
          <h1 className="text-3xl font-semibold text-slate-900">Minha equipe</h1>
          <p className="text-sm text-slate-500">
            Acompanhe tarefas, prazos e integrantes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
            <FaUserFriends />
            {hasMembers ? `${members.length} integrante${members.length > 1 ? "s" : ""}` : "Sem integrantes"}
          </div>
          <div className="flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
            <FaClipboardList />
            {totalActiveTasks} tarefa{totalActiveTasks === 1 ? "" : "s"} em andamento
          </div>
          {team?.manager?.name && (
            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600">
              <FaUsersCog />
              {team.manager.name}
            </div>
          )}
          {canCreateTeamEvent && (
            <button
              type="button"
              onClick={openTeamEventModal}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={eventSaving}
            >
              <FaCalendarPlus />
              Agendar evento
            </button>
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50 disabled:cursor-wait disabled:opacity-60"
          >
            <FaSync className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      {eventSuccess && (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-700 shadow-sm">
          {eventSuccess}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            key: "total",
            label: "Tarefas",
            value: summary.total,
            icon: FaTasks,
            iconClasses: "bg-emerald-100 text-emerald-600",
          },
          {
            key: "inProgress",
            label: "Em andamento",
            value: summary.inProgress,
            icon: FaPlayCircle,
            iconClasses: "bg-sky-100 text-sky-600",
          },
          {
            key: "overdue",
            label: "Em atraso",
            value: summary.overdue,
            icon: FaClock,
            iconClasses: "bg-rose-100 text-rose-600",
          },
          {
            key: "available",
            label: "Disponíveis",
            value: summary.available,
            icon: FaHandPaper,
            iconClasses: "bg-amber-100 text-amber-600",
          },
        ].map((card) => {
          const Icon = card.icon;
          const isActive =
            selectedStatus === card.key || (selectedStatus === "total" && card.key === "total");
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => handleStatusCardClick(card.key)}
              aria-pressed={isActive}
              className={`team-status-card w-full rounded-3xl border p-5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 ${
                isActive
                  ? "team-status-card--active border-emerald-400 bg-emerald-50 shadow-md"
                  : "border-slate-200 bg-white shadow-sm hover:border-emerald-300 hover:shadow"
              }`}
            >
              <div
                className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${card.iconClasses} ${
                  isActive ? "ring-2 ring-emerald-200" : ""
                }`}
              >
                <Icon />
              </div>
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{card.value}</p>
            </button>
          );
        })}
      </div>

      {hasMembers && (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Equipe em resumo</h2>
              <p className="text-sm text-slate-500">Veja quem precisa de atenção primeiro.</p>
            </div>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Ordenado por atrasos e carga
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {membersPerformanceRanking.slice(0, 6).map((member) => {
              const inProgress = member.performance?.inProgress || 0;
              const overdue = member.performance?.overdue || 0;
              const completed = member.performance?.completed || 0;
              return (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => setSelectedMember(String(member.id))}
                  className={`rounded-2xl border p-4 text-left transition hover:border-emerald-300 hover:bg-emerald-50 ${
                    String(selectedMember) === String(member.id)
                      ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-slate-900">{member.name}</h3>
                      <p className="truncate text-xs text-slate-500">
                        {member.group?.name || member.sector?.name || member.email || "Sem área definida"}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                        overdue ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {overdue ? `${overdue} atraso${overdue === 1 ? "" : "s"}` : "Em dia"}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <span className="rounded-xl bg-white px-3 py-2 font-medium text-slate-600">
                      {inProgress} em andamento
                    </span>
                    <span className="rounded-xl bg-white px-3 py-2 font-medium text-slate-600">
                      {completed} concluída{completed === 1 ? "" : "s"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {members.length > 6 && (
            <p className="mt-3 text-sm text-slate-500">
              Mostrando os 6 integrantes com maior prioridade. Use o filtro de integrante para encontrar os demais.
            </p>
          )}
        </section>
      )}

      <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Tarefas</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-500">
              Processo
              <select
                value={selectedProcess}
                onChange={(event) => setSelectedProcess(event.target.value)}
                className="max-w-72 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                <option value="all">Todos</option>
                {processOptions.map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.name}
                  </option>
                ))}
              </select>
            </label>
            {hasMembers && (
              <label className="flex items-center gap-2 text-sm text-slate-500">
                Responsável
                <select
                  value={selectedMember}
                  onChange={(event) => setSelectedMember(event.target.value)}
                  className="max-w-72 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="all">Todos</option>
                  {members.map((member) => (
                    <option key={member.id} value={String(member.id)}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        {filteredTasks.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">Processo</th>
                  <th scope="col" className="px-4 py-3">Equipe</th>
                  <th scope="col" className="px-4 py-3">Responsável atual</th>
                  <th scope="col" className="px-4 py-3">Prazo</th>
                  <th scope="col" className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTasks.map((task) => {
                  const {
                    instance,
                    context,
                    subordinates,
                    stepName,
                    deadline,
                    isOverdue,
                    status,
                    statusCategory,
                    endedAt,
                    isAvailable,
                  } = task;
                  const instanceId = instance?.id;
                  const processName = instance?.process?.name || "Processo";
                  const requester = instance?.startedBy?.name || instance?.startedBy?.email || "—";
                  const isCompleted = statusCategory === "completed";
                  let statusLabel = "Em andamento";
                  let statusClasses =
                    "inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700";
                  if (isCompleted) {
                    statusLabel = "Concluída";
                    statusClasses =
                      "inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-700";
                  } else if (status && status !== "ACTIVE") {
                    statusLabel = status
                      .toLowerCase()
                      .split("_")
                      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                      .join(" ");
                    statusClasses =
                      "inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600";
                  }
                  const deadlineLabel = isCompleted
                    ? endedAt
                      ? `Concluída em ${formatDateTime(endedAt)}`
                      : "Concluída"
                    : deadline
                    ? formatDateTime(deadline)
                    : "Sem prazo";
                  const deadlineClasses = isCompleted
                    ? "border border-violet-200 bg-violet-50 text-violet-700"
                    : isOverdue
                    ? "border border-rose-200 bg-rose-50 text-rose-700"
                    : "border border-emerald-200 bg-emerald-50 text-emerald-700";
                  const currentAssignee = instance?.currentAssignee?.name || instance?.currentAssignee?.email || "Sem responsável";
                  const canClaim = isAvailable;
                  const canRelease = !isCompleted && Boolean(context.canRelease);
                  return (
                    <tr key={instanceId} className="bg-white">
                      <td className="whitespace-nowrap px-4 py-4 align-top">
                        <div className="font-semibold text-slate-900">{processName}</div>
                        <div className="text-sm text-slate-500">{stepName}</div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                          <span className={statusClasses}>{statusLabel}</span>
                          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">
                            Iniciada por {requester} em {formatDateTime(instance?.startedAt)}
                          </span>
                        </div>
                      </td>
                      <td className="w-72 px-4 py-4 align-top">
                        {subordinates.length ? (
                          <div className="flex flex-col gap-2">
                            {subordinates.map((entry) => {
                              const displayName = entry.member?.name || entry.name || `Colaborador #${entry.id}`;
                              return (
                                <div
                                  key={`${instanceId}-member-${entry.id}`}
                                  className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
                                >
                                  <div className="font-medium text-slate-700">{displayName}</div>
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    {(entry.roles || []).map((role) => (
                                      <span
                                        key={role}
                                        className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[0.65rem] font-medium text-slate-500"
                                      >
                                        {ROLE_LABELS[role] || role}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">Sem integrantes atribuídos</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="font-medium text-slate-700">{currentAssignee}</div>
                        {context.canRelease ? (
                          <p className="text-xs text-slate-500">Pode liberar.</p>
                        ) : (
                          <p className="text-xs text-slate-400">Não libera.</p>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${deadlineClasses}`}>
                          {deadlineLabel}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex justify-end gap-2">
                          {canClaim && (
                            <button
                              type="button"
                              onClick={() => handleClaim(instanceId)}
                              disabled={actionLoading === instanceId}
                              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaHandPaper />
                              Assumir
                            </button>
                          )}
                          {canRelease && (
                            <button
                              type="button"
                              onClick={() => handleRelease(instanceId)}
                              disabled={actionLoading === instanceId}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-amber-300 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaUnlock />
                              Liberar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
            Nenhuma tarefa encontrada.
          </div>
        )}
      </section>

      {!hasMembers && (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
          Nenhum integrante vinculado.
        </div>
      )}

      <Modal open={eventModalOpen} onClose={closeTeamEventModal} size="max-w-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <FaCalendarPlus className="text-xl" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Agendar evento</h2>
            <p className="text-sm text-slate-500">
              Crie um lembrete para a equipe.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmitTeamEvent} className="space-y-4" aria-busy={eventSaving}>
          <div className="space-y-1">
            <label
              htmlFor="team-event-title"
              className="text-xs font-medium uppercase tracking-wide text-slate-600"
            >
              Título
            </label>
            <input
              id="team-event-title"
              type="text"
              value={eventForm.title}
              onChange={(e) => handleEventFieldChange("title", e.target.value)}
              disabled={eventSaving}
              maxLength={150}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder="Reunião de alinhamento"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="team-event-description"
              className="text-xs font-medium uppercase tracking-wide text-slate-600"
            >
              Descrição
            </label>
            <textarea
              id="team-event-description"
              rows={3}
              value={eventForm.description}
              onChange={(e) => handleEventFieldChange("description", e.target.value)}
              disabled={eventSaving}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder="Pauta ou instruções"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label
                htmlFor="team-event-date"
                className="text-xs font-medium uppercase tracking-wide text-slate-600"
              >
                Data
              </label>
              <input
                id="team-event-date"
                type="date"
                value={eventForm.date}
                onChange={(e) => handleEventFieldChange("date", e.target.value)}
                disabled={eventSaving}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
            {!eventForm.allDay && (
              <div className="space-y-1">
                <label
                  htmlFor="team-event-time"
                  className="text-xs font-medium uppercase tracking-wide text-slate-600"
                >
                  Horário
                </label>
                <input
                  id="team-event-time"
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => handleEventFieldChange("time", e.target.value)}
                  disabled={eventSaving}
                  step="300"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={eventForm.allDay}
                onChange={(e) => handleToggleAllDay(e.target.checked)}
                disabled={eventSaving}
                className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-400"
              />
              Dia inteiro
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={eventForm.notifyOnDashboard}
                onChange={(e) => handleEventFieldChange("notifyOnDashboard", e.target.checked)}
                disabled={eventSaving}
                className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-400"
              />
              Notificar no dashboard
            </label>
          </div>

          <p className="text-xs text-slate-500">
            Este evento será compartilhado com {memberIds.length} integrante
            {memberIds.length === 1 ? "" : "s"} da equipe.
          </p>

          {eventError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {eventError}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeTeamEventModal}
              disabled={eventSaving}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={eventSaving}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {eventSaving ? "Enviando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
